import type { JSX } from "react";
import { Text, View } from "@/components";
import type { Step } from "@/types/elearning/landing";

export interface StepsListProps {
  /** Pasos en orden; el número se deriva de la posición (1..n). */
  steps: Step[];
}

/**
 * @name StepsList
 * @description Lista ordenada de pasos con badge numerado y conector punteado
 * entre pasos (motivo "líneas/conectores" del manual de marca). Se reutiliza
 * en el "Paso a paso" del curso (Acerca del curso) y en las etapas para
 * obtener la LNC (Cómo obtener la licencia).
 *
 * @param {StepsListProps} props - Los pasos a renderizar.
 * @returns {JSX.Element} La lista numerada renderizada.
 */
const StepsList = ({ steps }: StepsListProps): JSX.Element => (
  <View type="ol" className="flex flex-col">
    {steps.map((step, index) => (
      <View
        key={step.title}
        type="li"
        className="relative flex items-start gap-4 pb-5 last:pb-0"
      >
        {index < steps.length - 1 && (
          <View
            aria-hidden="true"
            className="absolute left-4 top-9 bottom-1 border-l-2 border-dotted border-(--sv-sky)"
          />
        )}
        <View
          className="size-8 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
          role="presentation"
        >
          <Text type="span" className="text-sm font-semibold">
            {String(index + 1)}
          </Text>
        </View>
        <View className="flex flex-col gap-1 pt-1">
          <Text type="p" className="text-foreground/90 leading-relaxed">
            {step.title}
          </Text>
          {step.description && (
            <Text type="p" className="text-sm text-muted-foreground">
              {step.description}
            </Text>
          )}
        </View>
      </View>
    ))}
  </View>
);

export default StepsList;
